import MuiTabs from '@mui/material/Tabs';
import MuiTab from '@mui/material/Tab';
import MuiBox from '@mui/material/Box';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import type { ReactNode, KeyboardEvent } from 'react';

export interface TabItem {
  value: string;
  label: ReactNode;
  closable?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (value: string) => void;
  variant?: 'enclosed' | 'pill';
  onClose?: (value: string) => void;
}

// pill = a connected segmented control (hierarchy 2): a bordered, rounded container hugs
// its tabs (pillContainerSx), with hairline dividers between the segments. The active
// segment fills with a pale brand lavender and turns its leading icon + label brand-purple;
// inactive segments stay on the white container with a muted icon and a dark label.
// enclosed = browser-tab look (hierarchy 1). The label rules target `.MuiTypography-body2`:
// every tab label puts its primary text in a body2 <Text>.

// A clearer hairline than the faint 0.12 brand divider so the control's outline and its
// inner dividers read crisply at this size.
const segmentLine = (theme: Theme): string => `1px solid ${alpha(theme.palette.text.primary, 0.2)}`;

const pillContainerSx: SxProps<Theme> = (theme) => ({
  display: 'inline-flex',
  maxWidth: '100%',
  border: segmentLine(theme),
  borderRadius: 2, // 8px; the segments share it via overflow:hidden
  overflow: 'hidden',
  bgcolor: 'background.paper',
});

const pillSx: SxProps<Theme> = (theme) => ({
  minHeight: 36,
  '& .MuiTabs-indicator': { display: 'none' },
  '& .MuiTab-root': {
    minHeight: 36,
    px: 2,
    py: 0.75,
    textTransform: 'none',
    borderRadius: 0,
    borderRight: segmentLine(theme),
    color: 'text.secondary', // the leading icon inherits this muted tone when inactive
    '& .MuiTypography-body2': { fontWeight: 'bold' }, // hierarchy-2 labels are bold
    '&:last-of-type': { borderRight: 0 },
    // Active segment: pale brand lavender fill with a brand-purple icon and label.
    '&.Mui-selected': {
      bgcolor: alpha(theme.palette.primary.main, 0.12),
      color: 'primary.main',
      '& .MuiTypography-body2': { color: 'primary.main' },
    },
  },
});
const enclosedSx: SxProps<Theme> = {
  // 20px of air above the strip, and MUI's own 48px of tab below it, is what fills a 68px header
  // exactly — so the selected tab meets the header's bottom edge instead of floating above it, which
  // is the whole point of the enclosed look: the tab is continuous with the page it reveals. The
  // height is MUI's default and is deliberately NOT restated here; the inset is what was solved for
  // it. The header is pinned, so a label that outgrew 48px would make its tab spill out of the
  // chrome rather than push it down — which is why the session tab's two lines take `dense`.
  mt: 2.5,
  // The selected tab's underline (indicator) takes the tab's own gray bg instead of
  // the default primary, so no purple line shows beneath the active session tab.
  '& .MuiTabs-indicator': { bgcolor: 'background.default' },
  '& .MuiTab-root': {
    py: 0.75, // a little less vertical padding than MUI's 12px default
    textTransform: 'none',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    '& .MuiTypography-body2': { fontWeight: 'bold' }, // session-tab labels are always bold
    // Selected session tab takes the surface gray (background.default) — the same
    // gray as the content page it reveals — so it lifts off the white header and
    // reads as connected to the workspace below. Its primary label goes brand purple.
    '&.Mui-selected': {
      bgcolor: 'background.default',
      '& .MuiTypography-body2': { color: 'primary.main' },
    },
  },
};

export function Tabs({ items, value, onChange, variant = 'enclosed', onClose }: TabsProps) {
  const tabs = (
    <MuiTabs
      value={value}
      onChange={(_event, next: string) => {
        onChange(next);
      }}
      variant="scrollable"
      scrollButtons={false}
      sx={variant === 'pill' ? pillSx : enclosedSx}
    >
      {items.map((item) => (
        <MuiTab key={item.value} value={item.value} label={renderLabel(item, onClose)} />
      ))}
    </MuiTabs>
  );
  // The pill variant wraps the tabs in a bordered, rounded container (the segmented
  // control's outline); enclosed renders the tabs directly.
  return variant === 'pill' ? <MuiBox sx={pillContainerSx}>{tabs}</MuiBox> : tabs;
}

function renderLabel(item: TabItem, onClose?: (value: string) => void): ReactNode {
  if (!item.closable || !onClose) return item.label;
  const close = (): void => {
    onClose(item.value);
  };
  return (
    <MuiBox component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {item.label}
      {/* Close lives inside the tab button: render it as a clickable span (not a
          nested <button>, which is invalid) and stop propagation so closing
          never selects the tab. */}
      <MuiBox
        component="span"
        role="button"
        tabIndex={0}
        aria-label="Cerrar"
        onClick={(event) => {
          event.stopPropagation();
          close();
        }}
        onKeyDown={(event: KeyboardEvent<HTMLSpanElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.stopPropagation();
            event.preventDefault();
            close();
          }
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: 'pointer',
          opacity: 0.7,
          '&:hover': { opacity: 1 },
        }}
      >
        <CloseIcon fontSize="small" />
      </MuiBox>
    </MuiBox>
  );
}
